import { useEffect } from "react"
import { userData } from "@signals"

/**
 * Loads the rentacar-pms-agent chat widget script.
 *
 * IMPORTANT — access control:
 *   The widget is ONLY loaded for the users in `ALLOWED_USERS`. This is
 *   a first-line, UX-level gate; the widget's own backend
 *   (rentacar-pms-agent-*.run.app) MUST validate the incoming
 *   `data-username` against a server-side allowlist before returning
 *   any data — the DOM-side check can be bypassed by anyone with
 *   devtools.
 *
 * Behaviour:
 *   - No-op when the user is not logged in or is not `canarias-revenue`.
 *   - When it applies, appends one <script> to <body> with the
 *     data-app / data-username attributes the widget expects.
 *   - Removes the script on unmount OR when the user changes / logs
 *     out, so switching users tears the widget down.
 */
const AGENT_SRC       = 'https://rentacar-pms-agent-313881654678.us-central1.run.app/widget.js'
const ALLOWED_USERS   = ['canarias-revenue', 'europcar-kracon']
const SCRIPT_ELEMENT_ID = 'rentacar-pms-agent-widget'

export default function PmsAgentWidget() {
	const rawUsername = userData.value?.username ?? ''
	const username = String(rawUsername).trim().toLowerCase()
	const isAllowed = ALLOWED_USERS.includes(username)

	useEffect(() => {
		if (!isAllowed) {
			// Defensive: if a previous session as the allowed user left
			// the script behind, tear it down when a different user logs in.
			const stale = document.getElementById(SCRIPT_ELEMENT_ID)
			if (stale && stale.parentNode) stale.parentNode.removeChild(stale)
			return undefined
		}

		if (document.getElementById(SCRIPT_ELEMENT_ID)) return undefined

		const script = document.createElement('script')
		script.id           = SCRIPT_ELEMENT_ID
		script.src          = AGENT_SRC
		script.async        = true
		script.setAttribute('data-app', 'rentacarpms')
		// Bottom-right corner, where the support bubble used to be (the support chat lives in the header)
		script.setAttribute('data-position', 'right')
		script.setAttribute('data-username', rawUsername)  // pass verbatim, incl. case
		document.body.appendChild(script)

		return () => {
			const el = document.getElementById(SCRIPT_ELEMENT_ID)
			if (el && el.parentNode) el.parentNode.removeChild(el)
		}
	}, [isAllowed, rawUsername])

	return null
}
